import * as Phaser from "phaser"
import type { InputManager } from "../core/input-mapper"
import { THEME } from "../core/theme"
import { playClick, playHover } from "../core/ui-sfx"
import {
  type FilterableGameObject,
  applyGlowFilter,
  drawCornerAccents,
} from "./neon-fx"

const FONT = THEME.fonts
const COLOR = THEME.colors

export interface PanelConfig {
  x: number
  y: number
  width: number
  height: number
  color?: number
  radius?: number
  alpha?: number
  glowStrength?: number
  showBorder?: boolean
  showAccents?: boolean
  accentSize?: number
  useGradient?: boolean
  showInner?: boolean
}

export class RetroPanel extends Phaser.GameObjects.Container {
  protected bg: Phaser.GameObjects.Graphics
  protected border: Phaser.GameObjects.Graphics
  protected panelWidth: number
  protected panelHeight: number

  constructor(scene: Phaser.Scene, config: PanelConfig) {
    super(scene, config.x, config.y)

    const { width, height, color = 0x333344, alpha = 1.0 } = config

    this.panelWidth = width
    this.panelHeight = height

    this.bg = scene.add.graphics()
    this.drawRetroBackground(color, alpha)
    this.add(this.bg)

    this.border = scene.add.graphics()
    this.drawRetroBorder(color)
    this.add(this.border)

    this.setSize(width, height)
    scene.add.existing(this)
  }

  private drawRetroBackground(color: number, alpha: number): void {
    const gfx = this.bg
    gfx.clear()

    // Banded gradient background (SNES style)
    const steps = 4
    const h = this.panelHeight / steps

    for (let i = 0; i < steps; i++) {
      const stepColor = Phaser.Display.Color.ValueToColor(color)
      stepColor.darken(i * 10)
      gfx.fillStyle(stepColor.color, alpha)
      gfx.fillRect(0, i * h, this.panelWidth, h)
    }
  }

  private drawRetroBorder(color: number): void {
    const gfx = this.border
    gfx.clear()
    const w = this.panelWidth
    const h = this.panelHeight

    const highlight = Phaser.Display.Color.ValueToColor(color).lighten(30).color
    const shadow = Phaser.Display.Color.ValueToColor(color).darken(40).color
    const edge = 0x000000

    // Outer black edge
    gfx.lineStyle(1, edge, 1)
    gfx.strokeRect(0, 0, w, h)

    // Highlight (top and left)
    gfx.lineStyle(1, highlight, 1)
    gfx.lineBetween(1, 1, w - 2, 1)
    gfx.lineBetween(1, 1, 1, h - 2)

    // Shadow (bottom and right)
    gfx.lineStyle(1, shadow, 1)
    gfx.lineBetween(w - 2, 1, w - 2, h - 2)
    gfx.lineBetween(1, h - 2, w - 2, h - 2)
  }
}

export class NeonPanel extends Phaser.GameObjects.Container {
  protected bg: Phaser.GameObjects.Graphics
  protected border: Phaser.GameObjects.Graphics
  protected accents?: Phaser.GameObjects.Graphics
  protected scanline?: Phaser.GameObjects.Graphics
  protected panelWidth: number
  protected panelHeight: number

  constructor(scene: Phaser.Scene, config: PanelConfig) {
    super(scene, config.x, config.y)

    const {
      width,
      height,
      color = COLOR.panel,
      radius = 6,
      alpha = 0.5,
      glowStrength = 0,
      showBorder = true,
      showAccents = true,
      accentSize = 8,
      showInner = false,
    } = config

    this.panelWidth = width
    this.panelHeight = height

    this.bg = scene.add.graphics()

    // Main background
    this.bg.fillStyle(COLOR.black, alpha)
    this.bg.fillRoundedRect(0, 0, width, height, radius)

    // Optional inner box for richness (seen in character select)
    if (showInner) {
      this.bg.fillStyle(0x0d1325, 0.4)
      this.bg.fillRoundedRect(
        10,
        10,
        width - 20,
        height - 20,
        Math.max(0, radius - 4),
      )
    }
    this.add(this.bg)

    // Scanning effect
    this.scanline = scene.add.graphics()
    this.scanline.fillStyle(color, 0.05)
    this.scanline.fillRect(0, 0, width, 2)
    this.add(this.scanline)

    scene.tweens.add({
      targets: this.scanline,
      y: height,
      duration: 3000 + Math.random() * 2000,
      repeat: -1,
      ease: "Linear",
    })

    this.border = scene.add.graphics()
    if (showBorder) {
      // Use a more subtle border by default, matching the previous "wireframe" look
      this.border.lineStyle(1.5, color, 0.25)
      this.border.strokeRoundedRect(0, 0, width, height, radius)

      if (glowStrength > 0) {
        applyGlowFilter(this.border as unknown as FilterableGameObject, {
          color,
          outerStrength: glowStrength,
        })
      }
    }
    this.add(this.border)

    if (showAccents) {
      this.accents = scene.add.graphics()
      drawCornerAccents(
        this.accents,
        0,
        0,
        width,
        height,
        color,
        accentSize,
        0.6,
      )
      this.add(this.accents)
    }

    this.setSize(width, height)
    scene.add.existing(this)
  }

  protected updateBorder(color: number, alpha = 0.2, radius = 6): void {
    this.border.clear()
    this.border.lineStyle(1.5, color, alpha)
    this.border.strokeRoundedRect(
      0,
      0,
      this.panelWidth,
      this.panelHeight,
      radius,
    )
  }

  getBorderGraphics(): Phaser.GameObjects.Graphics {
    return this.border
  }
}

export interface ButtonConfig extends PanelConfig {
  text: string
  onClick: () => void
  inputManager: InputManager
  fontSize?: string
  textColor?: string
  glowColor?: number
  isDimmed?: boolean
  isFilled?: boolean
}

export class RetroButton extends RetroPanel {
  private label: Phaser.GameObjects.Text
  private hitZone: Phaser.GameObjects.Zone
  private isHovered = false
  private baseColor: number
  private baseTextColor: string
  private dimmedAlpha: number
  private isFilled: boolean
  private isEnabled = true
  private onClickHandler: () => void

  constructor(scene: Phaser.Scene, config: ButtonConfig) {
    super(scene, config)

    const {
      text,
      onClick,
      inputManager,
      width,
      height,
      fontSize = "8px",
      textColor = "#ffffff",
      color = 0x444455,
      isDimmed = false,
      isFilled = false,
    } = config

    this.baseColor = color
    this.baseTextColor = textColor
    this.dimmedAlpha = isDimmed ? 0.55 : 1
    this.isFilled = isFilled
    this.onClickHandler = onClick

    this.label = scene.add
      .text(width / 2, height / 2, text, {
        fontSize,
        fontFamily: FONT.pixel,
        color: textColor,
        wordWrap: { width: width - 16 },
        align: "center",
      })
      .setOrigin(0.5)
      .setAlpha(this.dimmedAlpha)
    this.add(this.label)

    this.redraw()

    this.hitZone = scene.add
      .zone(width / 2, height / 2, width, height)
      .setInteractive({ useHandCursor: true })
    this.add(this.hitZone)

    inputManager.bindPointer(this.hitZone, {
      pointerover: () => this.onHover(),
      pointerout: () => this.onOut(),
      pointerdown: () => {
        if (!this.isEnabled) return
        this.y += 1
        this.label.y += 1
      },
      pointerup: () => {
        if (!this.isEnabled) return
        this.y -= 1
        this.label.y -= 1
        playClick(scene)
        this.onClickHandler()
      },
    })
  }

  private redraw(): void {
    const color = this.isHovered
      ? Phaser.Display.Color.ValueToColor(this.baseColor).lighten(20).color
      : this.baseColor

    this.bg.clear()
    const steps = 4
    const h = this.panelHeight / steps
    for (let i = 0; i < steps; i++) {
      const stepColor = Phaser.Display.Color.ValueToColor(color)
      stepColor.darken(i * (this.isFilled ? 7 : 10))
      this.bg.fillStyle(stepColor.color, this.isFilled ? 1 : 0.95)
      this.bg.fillRect(0, i * h, this.panelWidth, h)
    }

    this.border.clear()
    const highlight = Phaser.Display.Color.ValueToColor(color).lighten(30).color
    const shadow = Phaser.Display.Color.ValueToColor(color).darken(40).color
    this.border.lineStyle(1, 0x000000, 1)
    this.border.strokeRect(0, 0, this.panelWidth, this.panelHeight)
    this.border.lineStyle(1, highlight, 1)
    this.border.lineBetween(1, 1, this.panelWidth - 2, 1)
    this.border.lineBetween(1, 1, 1, this.panelHeight - 2)
    this.border.lineStyle(1, shadow, 1)
    this.border.lineBetween(
      this.panelWidth - 2,
      1,
      this.panelWidth - 2,
      this.panelHeight - 2,
    )
    this.border.lineBetween(
      1,
      this.panelHeight - 2,
      this.panelWidth - 2,
      this.panelHeight - 2,
    )
  }

  private onHover(): void {
    if (this.isHovered || !this.isEnabled) return
    this.isHovered = true
    playHover(this.scene)
    this.label.setAlpha(1)
    this.redraw()
  }

  private onOut(): void {
    this.isHovered = false
    this.label.setAlpha(this.isEnabled ? this.dimmedAlpha : 0.4)
    this.redraw()
  }

  public updateColor(color: number): void {
    this.baseColor = color
    this.redraw()
  }

  public setSelected(selected: boolean): void {
    if (selected) {
      this.isHovered = true
      this.label.setAlpha(1)
      this.redraw()
    } else {
      this.isHovered = false
      this.label.setAlpha(this.dimmedAlpha)
      this.redraw()
    }
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled
    this.isHovered = false
    this.label.setAlpha(enabled ? this.dimmedAlpha : 0.4)
    this.label.setColor(enabled ? this.baseTextColor : COLOR.dimText)
    if (enabled) {
      this.hitZone.setInteractive({ useHandCursor: true })
    } else {
      this.hitZone.disableInteractive()
    }
    this.redraw()
  }

  public setText(text: string): void {
    this.label.setText(text)
  }

  public click(): void {
    if (!this.isEnabled) return
    playClick(this.scene)
    this.onClickHandler()
  }
}

export class RetroBar extends Phaser.GameObjects.Container {
  private gfx: Phaser.GameObjects.Graphics
  private barWidth: number
  private barHeight: number
  private barColor: number
  private currentPct = 1

  constructor(scene: Phaser.Scene, config: BarConfig) {
    super(scene, config.x, config.y)
    this.barWidth = config.width
    this.barHeight = config.height
    this.barColor = config.color
    this.gfx = scene.add.graphics()
    this.add(this.gfx)
    this.draw()
    scene.add.existing(this)
  }

  setPercent(pct: number): void {
    this.currentPct = Phaser.Math.Clamp(pct, 0, 1)
    this.draw()
  }

  private draw(): void {
    const { gfx, barWidth, barHeight, barColor, currentPct } = this
    gfx.clear()

    gfx.lineStyle(1, 0x000000, 1)
    gfx.strokeRect(0, 0, barWidth, barHeight)

    gfx.lineStyle(1, 0x333333, 1)
    gfx.strokeRect(1, 1, barWidth - 2, barHeight - 2)

    gfx.fillStyle(0x1a1a1a, 1)
    gfx.fillRect(2, 2, barWidth - 4, barHeight - 4)

    if (currentPct > 0) {
      const fillW = (barWidth - 6) * currentPct
      const color = Phaser.Display.Color.ValueToColor(barColor)

      gfx.fillStyle(color.clone().lighten(20).color, 1)
      gfx.fillRect(3, 3, fillW, barHeight / 2 - 1)

      gfx.fillStyle(color.clone().darken(20).color, 1)
      gfx.fillRect(3, 3 + barHeight / 2 - 1, fillW, barHeight / 2 - 2)

      gfx.lineStyle(1, 0x000000, 0.4)
      for (let x = 6; x < fillW; x += 6) {
        gfx.lineBetween(3 + x, 3, 3 + x, barHeight - 3)
      }
    }
  }
}

export interface BarConfig {
  x: number
  y: number
  width: number
  height: number
  color: number
  label?: string
  bgColor?: number
  radius?: number
  showSegments?: boolean
  useGhost?: boolean
}

export class NeonBar extends Phaser.GameObjects.Container {
  private gfx: Phaser.GameObjects.Graphics
  private barWidth: number
  private barHeight: number
  private barColor: number
  private currentPct = 1
  private ghostPct = 1
  private showSegments: boolean
  private useGhost: boolean

  constructor(scene: Phaser.Scene, config: BarConfig) {
    super(scene, config.x, config.y)

    this.barWidth = config.width
    this.barHeight = config.height
    this.barColor = config.color
    this.showSegments = config.showSegments ?? true
    this.useGhost = config.useGhost ?? true

    this.gfx = scene.add.graphics()
    this.add(this.gfx)

    if (config.label) {
      const label = scene.add
        .text(this.barWidth / 2, -10, config.label, {
          fontSize: "10px",
          fontFamily: FONT.sans,
          fontStyle: "900",
          color: COLOR.whiteTextSoft,
          letterSpacing: 1,
        })
        .setOrigin(0.5)
      this.add(label)
    }

    this.draw()
    scene.add.existing(this)
  }

  setPercent(pct: number, animate = true): void {
    const prevPct = this.currentPct
    this.currentPct = Phaser.Math.Clamp(pct, 0, 1)

    if (this.useGhost && this.currentPct < prevPct) {
      // Only update ghost if health dropped
      this.scene.tweens.add({
        targets: this,
        ghostPct: this.currentPct,
        duration: 800,
        delay: 400,
        ease: "Quad.easeOut",
        onUpdate: () => this.draw(),
      })
    } else {
      this.ghostPct = this.currentPct
    }

    if (animate) {
      this.scene.tweens.add({
        targets: this,
        // We use a dummy property for tweening the visual pct if we wanted smooth filling,
        // but for now we just redraw.
        duration: 300,
        ease: "Quad.easeOut",
        onUpdate: () => this.draw(),
      })
    } else {
      this.draw()
    }
  }

  setColor(color: number): void {
    this.barColor = color
    this.draw()
  }

  private draw(): void {
    const { gfx, barWidth, barHeight, barColor, currentPct, ghostPct } = this
    gfx.clear()

    // Background
    gfx.fillStyle(COLOR.black, 0.4)
    gfx.fillRect(0, 0, barWidth, barHeight)

    // Ghost bar (trailing damage)
    if (this.useGhost && ghostPct > currentPct) {
      gfx.fillStyle(COLOR.white, 0.3)
      gfx.fillRect(
        barWidth * currentPct,
        0,
        barWidth * (ghostPct - currentPct),
        barHeight,
      )
    }

    // Main fill
    if (currentPct > 0) {
      const fillW = barWidth * currentPct

      // Use the segmented/high-tech feel from before
      gfx.fillStyle(barColor, 0.8)
      gfx.fillRect(0, 0, fillW, barHeight)

      // Top highlight
      gfx.fillStyle(COLOR.white, 0.15)
      gfx.fillRect(0, 0, fillW, 2)

      // Segments
      if (this.showSegments) {
        gfx.lineStyle(1, COLOR.black, 0.2)
        const segmentW = 4
        for (let x = segmentW; x < fillW; x += segmentW) {
          gfx.lineBetween(x, 0, x, barHeight)
        }
      }
    }

    // Border
    gfx.lineStyle(1, barColor, 0.2)
    gfx.strokeRect(0, 0, barWidth, barHeight)
  }
}
